import { useCallback, useMemo } from 'react'
import { useBankAccounts, useFacilities } from '@/hooks/useBanking'
import type { FacilityFinancialAccountsScope } from '@/components/financial-accounts/financialAccountsScope'
import { findManualBankInScope, manualBanksInScope, type ManualBankInScope } from './manualBanks.utils'

/**
 * The manual banks of one Financial Accounts page — the list section and a
 * bank's detail page read the same data, so a bank listed there resolves here.
 *
 * Loads the FULL account list in both scopes, with every facility: what a page
 * lists is narrowed client-side (organization-wide to active facilities and
 * unassigned, ADR 0016; a facility's page to its own accounts, even when the
 * facility is inactive), while the bank-wide Edit and Delete reach every
 * account of the bank (see `manualBanksInScope`).
 *
 * `error` is set only for a load that never produced data: a failed background
 * refetch keeps the cached banks on screen.
 *
 * @param scope - The facility the page is scoped to; null on the organization-wide page
 */
export function useManualBanksInScope(scope: FacilityFinancialAccountsScope | null): {
  /** The banks the page lists: those with at least one account in scope. */
  banks: ManualBankInScope[]
  /** One bank by its exact stored name, also when the page lists none of its accounts. */
  findBank: (institutionName: string | null) => ManualBankInScope | null
  isLoading: boolean
  /** The account list is (re)loading. */
  isFetching: boolean
  error: Error | null
} {
  const accountsQuery = useBankAccounts()
  const facilitiesQuery = useFacilities({ activeOnly: false })
  const accounts = accountsQuery.data
  const facilities = facilitiesQuery.data
  const facilityId = scope?.facilityId ?? null

  // Without both lists nothing can be told apart, so nothing is listed.
  const banks = useMemo(
    () => (accounts && facilities ? manualBanksInScope(accounts, facilities, facilityId) : []),
    [accounts, facilities, facilityId],
  )
  const findBank = useCallback(
    (institutionName: string | null) =>
      accounts && facilities ? findManualBankInScope(accounts, facilities, facilityId, institutionName) : null,
    [accounts, facilities, facilityId],
  )

  return {
    banks,
    findBank,
    isLoading: accountsQuery.isLoading || facilitiesQuery.isLoading,
    isFetching: accountsQuery.isFetching,
    error: (accounts ? null : accountsQuery.error) ?? (facilities ? null : facilitiesQuery.error),
  }
}
